import type { Platform } from '@/lib/platform/platformDetection'
import type { NativeVersionUpdate } from '@/lib/nativeVersion'

/**
 * How hard the app pushes a native update, as a pure function of how long THIS DEVICE has
 * been ignoring it.
 *
 * WHY A LADDER AND NOT A DISMISS. The previous model stored a dismissed version string in
 * localStorage forever: one tap on the X and that release was never mentioned again, on day
 * 1 or on day 90. The user could make the app forget, permanently, by accident. That is the
 * ignore-forever hole this replaces — pressure now comes from RECURRENCE and SPECIFICITY,
 * never from removing the way out.
 *
 * WHY TIME CANNOT REACH `required`. Tiers 0-2 escalate on age; tier 3 blocks and is
 * reachable ONLY by an explicit `required` flag on the manifest entry. An app that blocks
 * users because a clock ran out is hostile, and the judgment about whether a gap actually
 * breaks something (protocol change, security fix) belongs to a human at release time, not
 * to a subtraction of dates. `daysSinceFirstSeen` therefore saturates at tier 2 by
 * construction, and there is a test that says so.
 *
 * WHY `firstSeenAt` IS DEVICE-LOCAL AND THAT IS CORRECT. We do not reliably know when a
 * build went live in a store (the manifest carries no date, and the App Store's is per
 * storefront). This measures how long THIS user has been declining, which is the thing
 * worth escalating on. A fresh install today starts at tier 0 for a week-old release — by
 * design, since that user has declined nothing.
 */
export type UpdateTier = 0 | 1 | 2 | 3

/**
 * `unknown` exists so the queue gate cannot fail open. A caller that has not yet heard from
 * the queue must NOT be able to pass `empty` by omission — sending a field user to the App
 * Store while unsent issues sit in the local queue risks exactly the work the offline queue
 * exists to protect, and "I have not measured" must never be spelled the same way as
 * "measured, and it is empty".
 *
 * `stuck` is the case that turned a protective gate into a trap. A permanently-failed entry
 * is still counted as unsent work (deliberately — see selectQueueState), but performSync()
 * skips permanent failures, so a queue holding nothing else CANNOT drain by waiting. With
 * only `pending` available, a required update against such a queue rendered a modal with no
 * close, no escape, no outside-click and no store link — a spinner saying "finishing sync"
 * over a sync that would never run again. Two individually-correct decisions composed into a
 * permanent lockout, which is why this needs its own name rather than a boolean.
 *
 * `unreadable` means the queue could not be inspected after bounded retries. It remains on
 * the gated side, but a required-update modal must release the store link because waiting
 * cannot prove that the queue drained.
 */
export type QueueState = 'empty' | 'pending' | 'stuck' | 'unknown' | 'unreadable'

export type UpdatePresentation = 'hidden' | 'card' | 'interstitial' | 'blocking'

export type UpdateDecision = {
  presentation: UpdatePresentation
  tier: UpdateTier
  /**
   * True only at tier 3, and only while waiting can still achieve something: show the modal
   * but withhold the store link until the queue drains. False when the queue is `stuck`,
   * because withholding then protects nothing and strands the user permanently.
   */
  waitingForQueue: boolean
  /**
   * True at tier 3 when the queue holds work that can never send itself. The modal keeps
   * blocking, but says so plainly and releases the store link: the queue lives in IndexedDB
   * and survives an app update, so the failed rows are still there afterwards to retry.
   */
  queueStuck: boolean
  /** True when bounded retries could not inspect the queue on this device. */
  queueUnreadable: boolean
  /** How long the "Later" button should silence this version, in ms. Zero when undismissable. */
  snoozeMs: number
}

const HOUR_MS = 60 * 60 * 1000
const DAY_MS = 24 * HOUR_MS

export const TIER_1_AFTER_DAYS = 3
export const TIER_2_AFTER_DAYS = 7

/** Snooze, not suppression: every value here is finite on purpose. */
export const SNOOZE_BY_TIER: Record<UpdateTier, number> = {
  0: 24 * HOUR_MS,
  1: 8 * HOUR_MS,
  2: 24 * HOUR_MS,
  3: 0,
}

const HIDDEN: UpdateDecision = {
  presentation: 'hidden',
  tier: 0,
  waitingForQueue: false,
  queueStuck: false,
  queueUnreadable: false,
  snoozeMs: 0,
}

/**
 * Age-driven tier. Saturates at 2 — see the header: the clock must not be able to block.
 */
export function tierForAge(firstSeenAt: number, now: number): UpdateTier {
  // A clock that moved backwards (timezone change, manual set) must not read as "ancient".
  const ageDays = Math.max(0, now - firstSeenAt) / DAY_MS

  if (ageDays >= TIER_2_AFTER_DAYS) return 2
  if (ageDays >= TIER_1_AFTER_DAYS) return 1
  return 0
}

export function presentationForTier(tier: UpdateTier): UpdatePresentation {
  if (tier === 3) return 'blocking'
  if (tier === 2) return 'interstitial'
  return 'card'
}

export type DecideUpdateInput = {
  platform: Platform
  availableUpdate: NativeVersionUpdate | null
  /** When this device first observed this exact version, in epoch ms. */
  firstSeenAt: number | null
  /** Epoch ms until which this exact version is silenced, or null. */
  snoozedUntil: number | null
  /** The version the snooze refers to; a snooze never carries over to a different version. */
  snoozedVersion: string | null
  queueState: QueueState
  now: number
}

export function decideUpdatePresentation({
  platform,
  availableUpdate,
  firstSeenAt,
  snoozedUntil,
  snoozedVersion,
  queueState,
  now,
}: DecideUpdateInput): UpdateDecision {
  if (platform === 'web' || availableUpdate === null) return HIDDEN

  // Required builds ignore the snooze entirely — that is the whole point of the flag — but
  // they still refuse to hand over the store link while work is unsent. The modal shows and
  // says it is finishing sync; it does not silently send the user away from their data.
  if (availableUpdate.required === true) {
    return {
      presentation: 'blocking',
      tier: 3,
      // 'stuck' is NOT a reason to keep waiting — nothing will send it. See QueueState.
      waitingForQueue: queueState === 'pending' || queueState === 'unknown',
      queueStuck: queueState === 'stuck',
      queueUnreadable: queueState === 'unreadable',
      snoozeMs: 0,
    }
  }

  const snoozeAppliesToThisVersion = snoozedVersion === availableUpdate.version
  if (snoozeAppliesToThisVersion && snoozedUntil !== null && now < snoozedUntil) return HIDDEN

  // No observation recorded yet means we are seeing it for the first time right now.
  const tier = tierForAge(firstSeenAt ?? now, now)

  // Anything louder than the quiet card waits for a drained queue. The quiet card is exempt:
  // it interrupts nothing and steals no focus, so gating it would only hide information.
  if (tier >= 1 && queueState !== 'empty') {
    return {
      presentation: 'card',
      tier: 0,
      waitingForQueue: false,
      queueStuck: false,
      queueUnreadable: false,
      snoozeMs: SNOOZE_BY_TIER[0],
    }
  }

  return {
    presentation: presentationForTier(tier),
    tier,
    waitingForQueue: false,
    queueStuck: false,
    queueUnreadable: false,
    snoozeMs: SNOOZE_BY_TIER[tier],
  }
}

/**
 * How long after the app becomes active an interstitial may still appear.
 *
 * The escalated modal is meant for "you just opened the app", never for "you have been
 * filling in a form for forty minutes". Nothing about the TIER distinguishes those two — the
 * ladder only knows the update's age — so without this window the hourly refetch in
 * useNativeVersionCheck could discover an update mid-task and throw a modal over an open
 * issue on a construction site. Outside the window the decision degrades to the quiet card,
 * which interrupts nothing and is still visible when the user next looks up.
 */
export const INTERSTITIAL_LAUNCH_GRACE_MS = 15_000

export function isInterstitialAllowed(
  lastActivationAt: number,
  now: number,
  graceMs: number = INTERSTITIAL_LAUNCH_GRACE_MS,
): boolean {
  const sinceActivation = now - lastActivationAt
  // A negative delta means the clock moved; treat it as "just activated" rather than as a
  // reason to suppress, since suppression here silently costs the user the escalation.
  return sinceActivation < graceMs
}
